import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { SaveLoader, BarLoader } from "@/_components";
import { useTheme, styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import Cookies from "universal-cookie";
// import "add-to-calendar-button";
import { atcb_action } from "add-to-calendar-button";
import useMediaQuery from "@mui/material/useMediaQuery";

const SubmitButton = styled(Button)({
  width: "100%",
  height: 50,
  backgroundColor: "#1c355e",
  color: "#fff",
  "&:hover": { backgroundColor: "#162a4b" },
});

const InputIcon = styled("span")({
  opacity: 0.6,
  color: "#1c355e",
  display: "flex",
  alignItems: "center",
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  marginTop: 16,
  padding: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4),
  },
}));

const MyAccount = ({ handleUpdate, submitting }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { storyName } = useParams();
  const cookies = new Cookies();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const appointmentData = JSON.parse(sessionStorage.getItem("appointmentData"));
  const enrollmentCodeData = cookies.get("lsEnrollmentCode");
  const isPartnerStory = storyName?.toLowerCase() === "partner";
  const partnerDisplayName =
    enrollmentCodeData?.partnerName ||
    enrollmentCodeData?.PartnerName ||
    enrollmentCodeData?.partner ||
    enrollmentCodeData?.Partner ||
    "";

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // useEffect(() => {
  //   const script = document.createElement("script");
  //   script.src = "https://cdn.jsdelivr.net/npm/add-to-calendar-button@2";
  //   script.async = true;
  //   document.body.appendChild(script);
  // }, []);
  const deviceTypes = {
    IID: "Ignition Interlock",
    PAM: "Portable Alcohol Monitor",
    ISA: "Intelligent Speed Assistance",
  };

  const validatePassword = (value) => {
    if (!value) return t("location.passwordRequired");
    if (
      !/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,24}$/.test(value)
    )
      return t("location.passwordPattern");
    return "";
  };

  const validateConfirmPassword = (value, pwd) => {
    if (!value) return t("location.confirmPasswordRequired");
    if (value !== pwd) return t("location.confirmPasswordValid");
    return "";
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    setErrors((prev) => ({
      ...prev,
      password: validatePassword(value),
      confirmPassword: validateConfirmPassword(confirmPassword, value),
    }));
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    setErrors((prev) => ({
      ...prev,
      confirmPassword: validateConfirmPassword(value, password),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!password) newErrors.password = t("location.passwordRequired");
    else if (
      !/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,24}$/.test(
        password,
      )
    )
      newErrors.password = t("location.passwordPattern");

    if (!confirmPassword)
      newErrors.confirmPassword = t("location.confirmPasswordRequired");
    else if (confirmPassword !== password)
      newErrors.confirmPassword = t("location.confirmPasswordValid");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    handleUpdate("MyAccount", { password });
  };

  const appointment = appointmentData;

  const resolveLocale = (lang) => {
    if (!lang) return "en-US";
    if (lang.startsWith("en")) return "en-US";
    if (lang.startsWith("es")) return "es-ES";
    return lang;
  };

  const formatAppointmentDate = (beginDT) => {
    if (!beginDT) return {};
    const locale = resolveLocale(i18n?.language);
    const date = new Date(beginDT);
    return {
      weekday: date.toLocaleDateString(locale, { weekday: "long" }),
      fullDate: date.toLocaleDateString(locale, {
        month: "long",
        day: "numeric",
      }),
      time: date.toLocaleTimeString(locale, {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const { weekday, fullDate, time } = formatAppointmentDate(
    appointment?.beginDT,
  );

  const formatAddressLine = (...parts) => parts.filter(Boolean).join(", ");

  const formatCityStateZip = (city, state, zip) => {
    const cityState = [city, state].filter(Boolean).join(", ");
    return [cityState, zip].filter(Boolean).join(" ").trim();
  };

  const start = appointment?.beginDT ? new Date(appointment.beginDT) : null;
  const end = start
    ? new Date(start.getTime() + (appointment.duration ?? 60) * 60000)
    : null;

  // const calendarBtnRef = useRef(null);

  const handlePaste = (event) => {
    event.preventDefault();
  };

  if (submitting) {
    return (
      <Grid
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
        }}
      >
        <BarLoader />
      </Grid>
    );
  }

  const vehicleText = appointment?.vehicleID
    ? `installation for ${appointment?.vehYear} ${appointment?.make} ${appointment?.model}`
    : "installation";

  const partnerDiscountValue =
    enrollmentCodeData?.partnerReferralDiscount ||
    enrollmentCodeData?.discountAmount ||
    enrollmentCodeData?.creditTotal;
  const partnerDiscountAmount = Number(
    String(partnerDiscountValue ?? "").replace(/[^0-9.-]/g, ""),
  );
  const formattedPartnerDiscount = Number.isFinite(partnerDiscountAmount)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Math.abs(partnerDiscountAmount))
    : "0";

  return (
    <Box textAlign="center" sx={{ maxWidth: 400 }}>
      {/* <Typography variant="h5" fontWeight="bold" color="#4da3ff">
        {t("location.booked")}
      </Typography>
      <Typography>{t("location.confirmationSent")}</Typography> */}

      {appointment ? (
        <StyledPaper elevation={2}>
          <Typography fontWeight="bold" fontSize={18}>
            {weekday}, {fullDate}
          </Typography>
          <Typography>
            {t("location.at")} {time}
          </Typography>
          <Typography mt={2} color="#707070">
            {t("location.youWillBeGoing")}
          </Typography>
          <Typography fontWeight="bold" fontSize={18}>
            {formatAddressLine(appointment.address1, appointment.address2)}
            <br />
            {formatCityStateZip(
              appointment.city,
              appointment.state,
              appointment.zip,
            )}
          </Typography>
          {appointment?.vehicleID != null ||
          appointment?.deviceType !== "PAM" ? (
            <Typography color="#707070">
              {t("location.installDevice", {
                deviceType: deviceTypes[appointment?.deviceType]?.toLowerCase(),
              })}
            </Typography>
          ) : (
            <Typography color="#707070">
              {t("location.installDevicePAM", {
                deviceType: deviceTypes[appointment?.deviceType]?.toLowerCase(),
              })}
            </Typography>
          )}
          {appointment?.vehicleID !== null ? (
            <Typography fontWeight="bold" fontSize={18}>
              {appointment.vehYear} {appointment.make} {appointment.model}
            </Typography>
          ) : (
            <></>
          )}
        </StyledPaper>
      ) : (
        <Grid
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
          }}
        >
          <BarLoader />
        </Grid>
      )}

      {/* <Grid sx={{ display: "flex", justifyContent: "center", mt: "8px" }}>
        {appointment && start && end && (
          <Box mt={1}>
            <add-to-calendar-button
              name={`${deviceTypes[appointment.deviceType]} installation for ${appointment.vehYear} ${appointment.make} ${appointment.model}`}
              description={`${deviceTypes[appointment.deviceType]} installation appointment for ${appointment.vehYear} ${appointment.make} ${appointment.model} in ${appointment.locationName}, ${appointment.city}, ${appointment.state}`}
              startDate={start.toISOString().split("T")[0]}
              startTime={start.toISOString().split("T")[1].slice(0, 5)}
              endDate={end.toISOString().split("T")[0]}
              endTime={end.toISOString().split("T")[1].slice(0, 5)}
              options="'Outlook.com','Google','iCal','Microsoft365','MicrosoftTeams','Yahoo'"
              buttonStyle="text"
              hideCheckmark
              label={`${t("location.addTo")} ${t("location.calendar")}`}
              customCss
            />
          </Box>
        )}
      </Grid> */}

      <Grid
        className="gradient-text"
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "8px",
        }}
      >
        <Button
          variant="text"
          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
          onClick={(e) => {
            if (!appointment || !start || !end) return;

            atcb_action(
              {
                name: `${deviceTypes[appointment.deviceType]} ${vehicleText}`,
                description: `${deviceTypes[appointment.deviceType]} ${vehicleText} in ${appointment.locationName}, ${appointment.city}, ${appointment.state}`,
                startDate: start.toISOString().split("T")[0],
                startTime: start.toISOString().split("T")[1].slice(0, 5),
                endDate: end.toISOString().split("T")[0],
                endTime: end.toISOString().split("T")[1].slice(0, 5),
                options: ["Outlook.com", "Google", "iCal", "Apple"],
                listStyle: "dropdown-static",
                timeZone: "currentBrowser",
              },
              e.currentTarget,
            );
          }}
        >
          <span style={{ fontWeight: "400" }}>{t("location.addTo")} </span>
          <span
            style={{ color: "#4da3ff", marginLeft: "4px", fontWeight: "400" }}
          >
            {t("location.calendar")}
          </span>
        </Button>

        {/* -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; */}
        {/* <add-to-calendar-button
          ref={calendarBtnRef}
          name={`${deviceTypes[appointment?.deviceType]} installation for ${appointment?.vehYear} ${appointment?.make} ${appointment?.model}`}
          description={`${deviceTypes[appointment?.deviceType]} installation appointment for ${appointment?.vehYear} ${appointment?.make} ${appointment?.model} in ${appointment?.locationName}, ${appointment?.city}, ${appointment?.state}`}
          startDate={start?.toISOString()?.split("T")[0]}
          startTime={start?.toISOString()?.split("T")[1]?.slice(0, 5)}
          endDate={end?.toISOString()?.split("T")[0]}
          endTime={end?.toISOString()?.split("T")[1]?.slice(0, 5)}
          options="'Outlook.com','Google','iCal','Microsoft365','MicrosoftTeams','Yahoo'"
          buttonStyle="text"
          hideCheckmark
          hideIconButton
          customCss
          styleLight="
            --btn-underline: none;
            --btn-hover-shadow: none;
            --btn-hover-background: none;
            --btn-background: none;
          "
        /> */}
      </Grid>

      <Box mt={2} py={3} px={2} bgcolor="#eef6ff" borderRadius={2}>
        <Typography fontWeight={600} fontSize={18} textAlign="left">
          {t("location.createAccount")}
        </Typography>
        <Typography mb={2} textAlign="left">
          {t("location.finalConfirmationDescription")}
        </Typography>

        <form onSubmit={onSubmit} style={{ marginTop: "16px" }}>
          <FormControl fullWidth>
            <InputLabel
              sx={{
                fontWeight: "bold",
                mt: "-20px",
                ml: "-14px",
                color: "#000",
              }}
            >
              {t("location.createPassword")}
            </InputLabel>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder={t("location.password")}
              value={password}
              onChange={handlePasswordChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InputIcon>
                      <LockOutlinedIcon />
                    </InputIcon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              sx={{
                mt: 3,
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff",
                },
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel
              sx={{
                fontWeight: "bold",
                mt: "-20px",
                ml: "-14px",
                color: "#000",
              }}
            >
              {t("location.confirmFinalPassword")}
            </InputLabel>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              onPaste={handlePaste}
              placeholder={t("location.password")}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InputIcon>
                      <LockOutlinedIcon />
                    </InputIcon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              sx={{
                mt: 3,
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff",
                },
              }}
            />
          </FormControl>

          <Box mt={2}>
            {submitting ? (
              <SaveLoader />
            ) : (
              <SubmitButton type="submit">
                {t("location.createAccountBtn")}
              </SubmitButton>
            )}
          </Box>
        </form>
      </Box>

      {isPartnerStory && !isMobile && (
        <Box mt={4} textAlign="left">
          <Typography fontWeight={900} fontSize={18} mb={1.2}>
            {t("partnerPricing.whatToExpectNextTitle")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
            <Box
              sx={{
                position: "relative",
                width: 24,
                height: 24,
                flexShrink: 0,
                color: "#75BE43",
              }}
            >
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: 22, position: "absolute", top: 1, left: 0 }}
              />
              <CheckIcon
                sx={{
                  fontSize: 14,
                  position: "absolute",
                  right: -2,
                  bottom: -2,
                  backgroundColor: "#fff",
                  borderRadius: "2px",
                }}
              />
            </Box>
            <Typography fontWeight={700} fontSize={16}>
              {t("partnerPricing.attendAppointmentTitle")}
            </Typography>
          </Box>
          <Box component="ul" sx={{ pl: 2.8, mt: 0, mb: 2.2 }}>
            <Typography component="li" sx={{ fontSize: 16, mb: 0.4 }}>
              {t("partnerPricing.attendAppointmentBullet1")}
            </Typography>
            <Typography component="li" sx={{ fontSize: 16 }}>
              {t("partnerPricing.attendAppointmentBullet2")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
            <HandshakeOutlinedIcon
              sx={{ color: "#75BE43", fontSize: 22, flexShrink: 0 }}
            />
            <Typography fontWeight={700} fontSize={16}>
              {t("partnerPricing.supportComplianceTitle")}
            </Typography>
          </Box>
          <Box component="ul" sx={{ pl: 2.8, mt: 0, mb: 3 }}>
            <Typography component="li" sx={{ fontSize: 16 }}>
              {t("partnerPricing.supportComplianceBullet1")}
            </Typography>
          </Box>

          <Typography fontWeight={700} fontSize={18} mb={1.2}>
            {t("partnerPricing.understandingPricingTitle", {
              deviceType: deviceTypes[appointment?.deviceType] ?? "",
            })}
          </Typography>

          <Typography fontWeight={700} fontSize={16} mb={0.5}>
            {t("partnerPricing.yourPartnerDiscountTitle")}
          </Typography>
          <Typography sx={{ fontSize: 16, mb: 2 }}>
            {t("partnerPricing.yourPartnerDiscountDescription", {
              discountAmount: formattedPartnerDiscount,
              partnerName: partnerDisplayName,
            })}
          </Typography>

          <Typography fontWeight={700} fontSize={16} mb={0.5}>
            {t("partnerPricing.atInstallationTitle")}
          </Typography>
          <Typography sx={{ fontSize: 16, mb: 2 }}>
            {t("partnerPricing.atInstallationDescription")}
          </Typography>

          <Typography fontWeight={700} fontSize={16} mb={0.5}>
            {t("partnerPricing.programCostTitle")}
          </Typography>
          <Typography sx={{ fontSize: 16 }}>
            {t("partnerPricing.programCostDescriptionPrefix")}
          </Typography>
          <Box component="ul" sx={{ pl: 2.8, mt: 0.5, mb: 2 }}>
            <Typography component="li" sx={{ fontSize: 16 }}>
              {t("partnerPricing.programCostBullet1", {
                deviceType:
                  deviceTypes[appointment?.deviceType]?.toLowerCase() || "",
              })}
            </Typography>
            <Typography component="li" sx={{ fontSize: 16 }}>
              {t("partnerPricing.programCostBullet2")}
            </Typography>
            <Typography component="li" sx={{ fontSize: 16 }}>
              {t("partnerPricing.programCostBullet3")}
            </Typography>
            <Typography component="li" sx={{ fontSize: 16 }}>
              {t("partnerPricing.programCostBullet4")}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

MyAccount.propTypes = {
  handleUpdate: PropTypes.func,
  submitting: PropTypes.bool,
};

const mapStateToProps = () => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(MyAccount);
